import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Mail, MapPin, Phone, Send, Facebook } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Mam kapitał — szukam projektu inwestycyjnego",
  "Mam grunt — chcę zwiększyć jego wartość",
  "Rozwijam sieć — szukam lokalizacji",
  "Szukam partnera do prowadzenia projektu",
  "Inny projekt inwestycyjny",
];

const EMPTY = { name: "", company: "", phone: "", email: "", project_type: "", message: "" };

const inputCls =
  "w-full border-b border-slate-300 bg-transparent py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#8C6D46] focus:outline-none transition-colors duration-300";

export const Contact = ({ scenario }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (scenario) setForm((f) => ({ ...f, project_type: scenario }));
  }, [scenario]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Wiadomość wysłana. Odezwę się najszybciej, jak to możliwe.");
      setForm(EMPTY);
    } catch {
      toast.error("Nie udało się wysłać wiadomości. Spróbuj ponownie lub zadzwoń.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-16">
        <Reveal className="lg:col-span-5">
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#8C6D46]">
            Kontakt
          </p>
          <h2 className="font-serif text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-3xl lg:text-4xl">
            Porozmawiajmy o projekcie
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Opisz krótko, czego szukasz lub jaki projekt planujesz — odezwę się z propozycją
            kolejnych kroków.
          </p>
          <div className="mt-10 space-y-5">
            <a
              href="tel:+48573569181"
              data-testid="contact-phone-link"
              className="flex items-center gap-4 text-sm text-slate-700 transition-colors hover:text-[#8C6D46]"
            >
              <Phone size={18} className="text-[#8C6D46]" /> +48 573 569 181
            </a>
            <a
              href="mailto:janusz@blotnicki.pl"
              data-testid="contact-email-link"
              className="flex items-center gap-4 text-sm text-slate-700 transition-colors hover:text-[#8C6D46]"
            >
              <Mail size={18} className="text-[#8C6D46]" /> janusz@blotnicki.pl
            </a>
            <a
              href="https://www.linkedin.com/in/janusz-b%C5%82otnicki-60b935244/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-center gap-4 text-sm text-slate-700 transition-colors hover:text-[#8C6D46]"
            >
              <Linkedin size={18} className="text-[#8C6D46]" /> LinkedIn — Janusz Błotnicki
            </a>
            <a
              href="https://www.facebook.com/people/Janusz-B%C5%82otnicki-Nieruchomo%C5%9Bci/61591745414359/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-link"
              className="flex items-center gap-4 text-sm text-slate-700 transition-colors hover:text-[#8C6D46]"
            >
              <Facebook size={18} className="text-[#8C6D46]" /> Facebook — Janusz Błotnicki Nieruchomości
            </a>
            <p className="flex items-center gap-4 text-sm text-slate-700">
              <MapPin size={18} className="text-[#8C6D46]" /> Polska południowo-wschodnia
            </p>
          </div>
          <div className="mt-12 flex items-center gap-5 border-t border-slate-200 pt-8">
            <img
              src="/assets/portrait-suit.webp"
              alt="Janusz Błotnicki"
              data-testid="contact-portrait-image"
              className="h-20 w-20 rounded-full border-2 border-[#8C6D46]/30 object-cover object-top"
            />
            <div>
              <p className="font-serif text-lg font-medium text-slate-900">Janusz Błotnicki</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Investment &amp; Expansion Partner
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-sm border border-slate-200 bg-white p-8 lg:p-10"
          >
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Imię i nazwisko *"
                className={inputCls}
              />
              <input
                data-testid="contact-company-input"
                value={form.company}
                onChange={set("company")}
                placeholder="Firma / podmiot"
                className={inputCls}
              />
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Telefon kontaktowy"
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Adres e-mail *"
                className={inputCls}
              />
            </div>
            <select
              data-testid="contact-project-type-select"
              value={form.project_type}
              onChange={set("project_type")}
              className={`${inputCls} mt-8 ${form.project_type ? "" : "text-slate-400"}`}
            >
              <option value="">Typ projektu / obszar zainteresowania</option>
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t} className="text-slate-900">
                  {t}
                </option>
              ))}
            </select>
            <textarea
              data-testid="contact-message-textarea"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Opisz krótko, czego szukasz lub jaki projekt planujesz *"
              className={`${inputCls} mt-8 resize-none`}
            />
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-10 flex items-center gap-3 rounded-full bg-[#090D16] px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#8C6D46] disabled:opacity-60"
            >
              {sending ? "Wysyłanie…" : "Wyślij wiadomość"}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
